import React, { useContext, useState, useEffect } from 'react';
import classNames from 'classnames';
import type { UseFormHandleSubmit } from 'react-hook-form';

import Heading from 'components/partials/heading/heading';
import Button from 'components/partials/button/button';
import NotificationMessage from 'components/partials/notification/notification';

import PersonalInformationForm from 'containers/admin/users/user/forms/personal-info-form/personal-info-form';
import UserDetailsForm from 'containers/admin/users/user/forms/user-details-form/user-details-form';
import { UserProfileContext } from 'containers/admin/users/user/user-profile/user-profile';

interface ClientLike {
  id?: string | number;
  name?: string;
  authorizedDomains?: string;
  [key: string]: unknown;
}

interface UserFormProps {
  isMVUser?: boolean;
  userRole?: string;
  currentClient?: ClientLike | null;
  isFormDisabled?: boolean;
  isSelectDisabled?: boolean;
  isNotNewUser?: boolean;
  isCurrentUserProfile?: boolean;
  onCancel: () => void;
  onSubmit: (formData: Record<string, unknown>) => void;
  handleSubmit: UseFormHandleSubmit<Record<string, unknown>>;
}

const UserForm = ({
  isMVUser,
  userRole,
  currentClient,
  isFormDisabled,
  isSelectDisabled,
  isNotNewUser,
  isCurrentUserProfile,
  onCancel,
  onSubmit,
  handleSubmit,
}: UserFormProps): React.JSX.Element => {
  const { errors } = useContext(UserProfileContext);
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState<string | undefined>(
    userRole
  );

  const onEditModeStyles = classNames('flex', {
    'flex-col': isFormDisabled || isNotNewUser,
  });

  useEffect(() => {
    setIsSaveBtnDisabled(Object.keys(errors).length !== 0);
  }, [Object.keys(errors).length]);

  return (
    <div className="w-full">
      <div className="flex w-full">
        {!isCurrentUserProfile && (
          <Heading variant="h3" customClass="w-3/6 mr-16 mt-4">
            Personal information
          </Heading>
        )}
        {!isFormDisabled && !isNotNewUser && (
          <Heading variant="h3" customClass="w-3/6 mt-4">
            User details
          </Heading>
        )}
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col w-full">
        <div className={onEditModeStyles}>
          <PersonalInformationForm
            {...{
              isNotNewUser,
              isFormDisabled,
              isMVUser,
            }}
          />
          <UserDetailsForm
            {...{
              currentUserRole,
              currentClient: currentClient as never,
              isNotNewUser,
              isFormDisabled,
              isSelectDisabled,
              isMVUser,
              isCurrentUserProfile,
            }}
          />
          {!isFormDisabled && isMVUser && (
            <div className="flex">
              <NotificationMessage
                heading="Note"
                customClass="mb-5"
                variant="info"
                message="Locked items cannot be edited. Contact your institution administrator or MARKETview Consultant to make changes."
              />
            </div>
          )}
        </div>
        {!isFormDisabled && (
          <div className="flex justify-end mt-4">
            <Button
              type="button"
              variant="reserve-text"
              size="md"
              onClick={(e) => {
                e.preventDefault();
                setCurrentUserRole(userRole);
                onCancel();
              }}
              data-testid="cancel-user-form-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaveBtnDisabled}
              variant="brand-filled"
              size="md"
              data-testid="save-user-form-button"
            >
              Save changes
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default UserForm;
